import { FC, useEffect, useState } from 'react';
import { v4 as uuid } from 'uuid';

import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import MetadataFormAccordion from '@/shared/components/forms/MetadataField/components/MetadataFormAccordion';
import Metadata from '@/shared/types/metadata/index.type';

type ProductVariantsFormProps = {
  metadata: Metadata;
  initialData?: Record<string, unknown>[];
  getVariants: (
    values: {
      variant_id: string;
      isNotRemovable?: boolean;
      required: Record<string, unknown>;
      optional: Record<string, unknown>;
    }[],
  ) => void;
};

const ProductVariantsForm: FC<ProductVariantsFormProps> = ({
  metadata,
  initialData,
  getVariants,
}) => {
  const [variants, setVariants] = useState<
    {
      variant_id: string;
      isNotRemovable?: boolean;
      required: Record<string, unknown>;
      optional: Record<string, unknown>;
    }[]
  >([{ variant_id: uuid(), required: {}, optional: {} }]);

  const onVariantsDidUpdate = (details: typeof variants) => {
    getVariants(details);
  };

  const handleAddVariant = () => {
    const newValues = [
      ...variants,
      {
        variant_id: uuid(),
        isNotRemovable: false,
        required: {},
        optional: {},
      },
    ];
    setVariants(newValues);
    onVariantsDidUpdate(newValues);
  };

  const handleRemoveVariant = (variant_id: string) => {
    const newValues = variants.filter((item) => item.variant_id !== variant_id);
    setVariants(newValues);
    onVariantsDidUpdate(newValues);
  };

  const onUpdateVariantProduct = (
    value: Record<string, unknown>,
    variant_id: string,
  ) => {
    const newValues = variants.map((item) =>
      item.variant_id === variant_id
        ? { ...item, required: { ...item.required, ...value } }
        : item,
    );
    setVariants(newValues);
    onVariantsDidUpdate(newValues);
  };

  const onUpdateVariantProductDetails = (
    value: Record<string, unknown>,
    variant_id: string,
  ) => {
    const newValues = variants.map((item) =>
      item.variant_id === variant_id
        ? { ...item, optional: { ...item.optional, ...value } }
        : item,
    );
    setVariants(newValues);
    onVariantsDidUpdate(newValues);
  };

  useEffect(() => {
    if (initialData) {
      const requiredMetadata = metadata.metadata_definitions.filter(
        (item) =>
          item.required_level === 'REQUIRED' &&
          item.level_constraint === 'VARIANT',
      );

      const optionalMetadata = metadata.metadata_definitions.filter(
        (item) =>
          item.required_level === 'OPTIONAL' &&
          item.level_constraint === 'VARIANT',
      );

      const newVariants = initialData
        .filter((item) => item.variant_name !== '_default')
        .map((data) => {
          const required = requiredMetadata.reduce(
            (acc, meta) => {
              if (
                data[meta.metadata_key] !== null ||
                data[meta.metadata_key] !== undefined
              ) {
                acc[meta.metadata_key] = data[meta.metadata_key];
              }
              return acc;
            },
            {} as Record<string, unknown>,
          );

          const optional = optionalMetadata.reduce(
            (acc, meta) => {
              if (
                data[meta.metadata_key] !== null ||
                data[meta.metadata_key] !== undefined
              ) {
                acc[meta.metadata_key] = data[meta.metadata_key];
              }
              return acc;
            },
            {} as Record<string, unknown>,
          );

          return {
            variant_id: data.variant_id as string,
            isNotRemovable: true,
            required,
            optional,
          };
        });

      setVariants(newVariants);
    }
  }, [initialData, metadata.metadata_definitions]);

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant='body1' mb={2}>
        Variants
      </Typography>

      <Stack spacing={2}>
        {variants.map((variant) => (
          <MetadataFormAccordion
            key={variant.variant_id}
            accordionProps={{
              title: (variant.required.variant_name as string)
                ? `Variant Name: ${variant.required.variant_name as string}`
                : 'Variant Name: -',
            }}
            metadata={metadata}
            fields={{
              required: variant.required,
              optional: variant.optional,
            }}
            initialData={{ ...variant.required, ...variant.optional }}
            isRemoveIconDisabled={variants.length < 2 || variant.isNotRemovable}
            levelConstraint='VARIANT'
            getValues={({ required, optional }) => {
              if (required !== undefined) {
                return onUpdateVariantProduct(required, variant.variant_id);
              }

              if (optional !== undefined) {
                return onUpdateVariantProductDetails(
                  optional,
                  variant.variant_id,
                );
              }
            }}
            onRemoveIconClick={() => handleRemoveVariant(variant.variant_id)}
          />
        ))}
      </Stack>

      <Stack direction='row' spacing={1} mt={2}>
        <Button variant='text' size='small' onClick={handleAddVariant}>
          add variant
        </Button>
      </Stack>
    </Paper>
  );
};

export default ProductVariantsForm;
